import { ButtonSizeEnum, ButtonTypeEnum } from "@/components/atoms/Button";
import {
  BookADemoSourceEnum,
  CtaActionType,
  CtaType,
  SignUpSourceEnum,
} from "@/components/molecules/CtaButtonGroup";
import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import { ProductV2FaqItem, ProductV2StoryCard } from "./product.v2.data";
import {
  AiTeamProps,
  AutomationsProps,
  CtaBannerV2Props,
  IdeaToIncomeProps,
} from "./home.v2.data";
import { getCDNURL } from "@/shared/utils/commons";

/**
 * /page/learning-management-system — data contract.
 *
 * Images live on the CDN under `graphy-web/V4/images/LMS Page/…` with
 * the same section-folder structure shipped in `public/images/images/
 * LMS Page/` as a local fallback.
 *
 * Each section reuses an existing V2 primitive (AccordionWithMediaSection,
 * ProductCardGridSection, AiTeamSection, StoriesMarqueeV2, FaqV2,
 * CtaBannerV2) except the Hero, which is a page-local component with a
 * metric-pill row + product mockup.
 */

const LMS_BASE = `${getCDNURL()}graphy-web/V4/images/LMS Page`;

/* ── Shared CTAs ──────────────────────────────────────────────────── */

const heroCtas: CtaType[] = [
  {
    type: ButtonTypeEnum.Secondary,
    size: ButtonSizeEnum.Regular,
    text: "Start Your Free Trial",
    actionType: CtaActionType.SignUp,
    amplitudeEvent: {
      eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
      eventProperties: { source: SignUpSourceEnum.hero },
    },
  },
  {
    type: ButtonTypeEnum.Primary,
    size: ButtonSizeEnum.Regular,
    text: "Book A Demo",
    actionType: CtaActionType.BookDemo,
    amplitudeEvent: {
      eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
      eventProperties: { source: BookADemoSourceEnum.hero },
    },
  },
];

const bodyCtas: CtaType[] = [
  {
    type: ButtonTypeEnum.Secondary,
    size: ButtonSizeEnum.Regular,
    text: "Start Your Free Trial",
    actionType: CtaActionType.SignUp,
    amplitudeEvent: {
      eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
      eventProperties: { source: SignUpSourceEnum.body_end },
    },
  },
  {
    type: ButtonTypeEnum.Primary,
    size: ButtonSizeEnum.Regular,
    text: "Book A Demo",
    actionType: CtaActionType.BookDemo,
    amplitudeEvent: {
      eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
      eventProperties: { source: BookADemoSourceEnum.bodyEnd },
    },
  },
];

/* ── Hero data ────────────────────────────────────────────────────── */

export type LmsHeroStat = { label: string; delta: string };

export type LmsHeroProps = {
  eyebrow: string;
  titleMuted: string;
  titleBright: string;
  subtitle: string;
  ctas: CtaType[];
  stats: LmsHeroStat[];
  image: string;
  imageMobile?: string;
};

const hero: LmsHeroProps = {
  eyebrow: "LMS",
  titleMuted: "AI-powered LMS Built to",
  titleBright: "Scale Your Academy",
  subtitle:
    "From hosting training programs to marketing tools, Graphy has everything you need to build a profitable education business.",
  ctas: heroCtas,
  stats: [
    { label: "Engagement", delta: "+18%" },
    { label: "Transaction", delta: "+23%" },
    { label: "Revenue", delta: "+45%" },
  ],
  image: `${LMS_BASE}/Hero Section/hero section.png`,
};

/* ── S2 · Learning Experiences (accordion + media, image right) ───── */

const learningExperiences: AutomationsProps = {
  title:
    "Create Learning Experiences\nYour Learners Love",
  subtitle:
    "From onboarding to upskilling - Graphy turns your training programs into structured, engaging learning experiences.",
  imagePosition: "right",
  features: [
    {
      id: "engaging-training",
      title: "Engaging & Effective Training",
      description:
        "Support your learners with interactive videos, live sessions, and graded assessments.",
      image: `${LMS_BASE}/Create Learning Experiences Your Learners Love/Engaging & Effective Training.png`,
    },
    {
      id: "versatile-delivery",
      title: "Versatile Course Delivery",
      description:
        "Run self-paced modules, live cohorts, or blended programs — deliver any format that fits your learners and their schedule.",
      image: `${LMS_BASE}/Create Learning Experiences Your Learners Love/Versatile Course Delivery.png`,
    },
    {
      id: "track-completion",
      title: "Track Completion & Compliance",
      description:
        "Monitor learner progress, completion rates, and issue verifiable certificates — so every program stays on track and audit-ready.",
      image: `${LMS_BASE}/Create Learning Experiences Your Learners Love/Track Completion & Compliance.png`,
    },
  ],
};

/* ── S3 · Sell Globally (3-card grid + CTAs) ──────────────────────── */

const sellGlobally: IdeaToIncomeProps = {
  title: "Sell Globally. Payments Built for Modern Academies.",
  subtitle:
    "Complex payments kill conversions. Graphy gives you flexible pricing options, global gateways, and automated tax handling so you can focus on teaching.",
  ctas: bodyCtas,
  columns: 3,
  cards: [
    {
      title: "Flexible Pricing Models",
      description:
        "Sell one-time courses, structured payment plans, bundles, or recurring memberships - all from one place.",
      image: `${LMS_BASE}/Sell Globally Section/Flexible Pricing Models.png`,
    },
    {
      title: "Sell Worldwide, Get Paid Locally",
      description:
        "Accept payments effortlessly in 120+ currencies using global and local gateways.",
      image: `${LMS_BASE}/Sell Globally Section/Sell Worldwide, Get Paid Locally..png`,
    },
    {
      title: "Automated Taxes & Invoices",
      description:
        "Focus on your curriculum while taxes and invoices are handled completely automatically - no spreadsheets needed.",
      image: `${LMS_BASE}/Sell Globally Section/Automated Taxes & Invoices..png`,
    },
  ],
};

/* ── S4 · Marketing Funnels (accordion + media, image left) ───────── */

const marketingFunnels: AutomationsProps = {
  title: "Profitable Marketing Funnels\nto Increase Your ROI",
  subtitle:
    "Turn your knowledge into a revenue engine. Start with an idea, and Graphy provides powerful tools to convert visitors into loyal learners.",
  imagePosition: "left",
  features: [
    {
      id: "white-label-lms",
      title: "A True White-Label LMS",
      description:
        "Use your own domain name, imprint your brand identity, and take full control of the customer experience from end to end.",
      image: `${LMS_BASE}/Profitable Marketing Section/True White-Label LMS.png`,
    },
    {
      id: "upsell-cross-sell",
      title: "Upsell and Cross-sell",
      description:
        "Recommend the next course, bundle, or membership at checkout and inside every lesson — grow revenue per learner without new acquisition spend.",
      image: `${LMS_BASE}/Profitable Marketing Section/Upsell and Cross-sell.png`,
    },
    {
      id: "seamless-integrations",
      title: "Seamless Integrations",
      description:
        "Connect Zapier, Google Analytics, Meta Pixel, and your favourite CRM — plug Graphy into the martech stack you already run.",
      image: `${LMS_BASE}/Profitable Marketing Section/Seamless Integration.png`,
    },
  ],
};

/* ── S5 · AI Team (dark section) ──────────────────────────────────── */

const aiTeam: AiTeamProps = {
  eyebrow: "SCALE",
  titleMuted: "Your AI Team that",
  titleBright: "Builds, Sells & Scales",
  subtitle:
    "Other platforms give you tools. Graphy gives you intelligence. Every part of your business learns, improves, and works harder over time—so you can succeed faster with less effort.",
  ctas: bodyCtas,
  cards: [
    {
      kind: "ai-operator",
      badge: "Launch 80% faster",
      title: "AI Operator",
      description: "Build your entire teaching business in hours",
      image: `${LMS_BASE}/Scale Section Images/AI Operator.png`,
    },
    {
      kind: "ai-agents",
      badge: "Convert 5X more visitors",
      title: "AI Agents",
      description: "Sales, support, and marketing—handled instantly",
      image: `${LMS_BASE}/Scale Section Images/AI Agents.png`,
    },
    {
      kind: "ai-avatar",
      badge: "Monetize 1:1 interactions",
      title: "AI Avatar",
      description: "Your audience gets personal tutoring, instantly",
      image: `${LMS_BASE}/Scale Section Images/AI Avatar.png`,
    },
  ],
};

/* ── S6 · Stories marquee ─────────────────────────────────────────── */

const stories: ProductV2StoryCard[] = [
  {
    title: "My Go-To Platform",
    quote:
      "Webveda, a 5 lakh+ student community is run by 1 full-time employee and 8 interns only. This is made possible only by Graphy. Right from conducting courses, assignments, live sessions to progress tracking, Graphy handles everything for us.",
    name: "Ankur Warikoo",
    role: "Coach",
    avatar: `${getCDNURL()}graphy-web/V4/images/Testimonial Images/Ankur Warikoo.png`,
  },
  {
    title: "Everything In One Place",
    quote:
      "With Graphy, we've scaled our programs to reach aspiring professionals across countries — all from one platform. The ease of managing course content, payments, and learner communication has allowed us to focus more on mentorship and less on operations.",
    name: "Aarushi Bedi",
    role: "Founder | The Fluent Flyers",
    avatar: `${getCDNURL()}graphy-web/V4/images/Testimonial Images/Aarushi Bedi.png`,
  },
  {
    title: "Reliable Partner",
    quote:
      "This is a human business. You don't choose a platform because it's cheap or fancy. You choose people you can depend on. And Graphy has been that partner for me.",
    name: "Atish Mathur",
    role: "Educator",
    avatar: `${getCDNURL()}graphy-web/V4/images/Testimonial Images/Athish Mathur.png`,
  },
  {
    title: "Selling Made Simple",
    quote:
      "Graphy has made my life easier. I can finally focus on what I do best — teaching — while the platform takes care of everything else.",
    name: "Dr. Anand Mani",
    role: "Coach",
    avatar: `${getCDNURL()}graphy-web/V4/images/Testimonial Images/Dr. Anand Mani.png`,
  },
];

/* ── S7 · FAQ ─────────────────────────────────────────────────────── */

const faqItems: ProductV2FaqItem[] = [
  {
    question: "Is Graphy suitable for corporate training and employee onboarding?",
    answer:
      "Yes. Graphy is built for both creators and enterprises — teams use it to run onboarding, compliance, and upskilling programs with structured learning paths, progress tracking, and custom branding.",
  },
  {
    question: "Does Graphy support compliance tracking and certifications?",
    answer:
      "Yes. You can issue verifiable certificates on course completion, track learner progress against required modules, and export audit-ready reports for compliance teams.",
  },
  {
    question: "Can I track individual learner progress and performance?",
    answer:
      "Absolutely. Graphy's analytics surface completion rates, quiz scores, watch time, and engagement per learner — so you know exactly who's on track and where to step in.",
  },
  {
    question: "Does Graphy integrate with tools my company already uses?",
    answer:
      "Yes. Graphy connects with Zapier, Google Analytics, Meta Pixel, popular CRMs, and payment gateways out of the box — and offers APIs for deeper custom integrations.",
  },
];

const faqCtas: CtaType[] = [
  {
    type: ButtonTypeEnum.Secondary,
    size: ButtonSizeEnum.Regular,
    text: "Start Your Free Trial",
    actionType: CtaActionType.SignUp,
    amplitudeEvent: {
      eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
      eventProperties: { source: SignUpSourceEnum.body_end },
    },
  },
  {
    type: ButtonTypeEnum.Primary,
    size: ButtonSizeEnum.Regular,
    text: "Book A Demo",
    actionType: CtaActionType.BookDemo,
    amplitudeEvent: {
      eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
      eventProperties: { source: BookADemoSourceEnum.bodyEnd },
    },
  },
];

/* ── S8 · CTA banner ──────────────────────────────────────────────── */

const ctaBanner: CtaBannerV2Props = {
  titleMuted: "Ready to Build",
  titleBright: "Your Creator Business?",
  description:
    "Launch faster, sell more, & work less with intelligent automation. Join 200,000+ creators who chose the smarter way.",
  ctas: bodyCtas,
  backgroundImage: `${getCDNURL()}graphy-web/V4/images/Home Page/CTA Section/Footer Banner.png`,
  backgroundImageMobile: `${getCDNURL()}graphy-web/V4/images/Mobile/Home Page/Footer Banner/Banner.png`,
};

/* ── Exported page data ───────────────────────────────────────────── */

export type LmsV2DataType = {
  hero: LmsHeroProps;
  learningExperiences: AutomationsProps;
  sellGlobally: IdeaToIncomeProps;
  marketingFunnels: AutomationsProps;
  aiTeam: AiTeamProps;
  stories: ProductV2StoryCard[];
  storiesCtas: CtaType[];
  faqItems: ProductV2FaqItem[];
  faqCtas: CtaType[];
  ctaBanner: CtaBannerV2Props;
};

export const lmsV2Data: LmsV2DataType = {
  hero,
  learningExperiences,
  sellGlobally,
  marketingFunnels,
  aiTeam,
  stories,
  storiesCtas: bodyCtas,
  faqItems,
  faqCtas,
  ctaBanner,
};
